import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import { Box, Flex, Button, Icon, Spinner } from "@chakra-ui/react";
import { FaDesktop, FaMobileAlt } from "react-icons/fa";
import { useEffect, useRef } from "react";

const initialSrcDoc = ` 
    <html>
      <head>
        <script>
          let isReady = false;
          
          // Signal that iframe is ready to receive messages
          window.addEventListener('load', () => {
            isReady = true;
            window.parent.postMessage({ type: 'iframe-ready' }, '*');
          });

          window.addEventListener('message', (event) => {
            const { type, value } = event.data;

            if (type === 'html') {
              document.body.innerHTML = value;
              
              // Always send rendered message, regardless of content
              window.parent.postMessage({ type: 'rendered' }, '*');
            }
          });

          // Ensure we're ready even if load event already fired
          if (document.readyState === 'complete') {
            setTimeout(() => {
              if (!isReady) {
                isReady = true;
                window.parent.postMessage({ type: 'iframe-ready' }, '*');
              }
            }, 0);
          }
        </script>
      </head>
      <body style="height: 100%;"></body>
    </html>
  `;

const CreateCampaignPreview = ({
  showPreview,
  emailHtml,
  emailHtmlCounter,
  view,
  setView,
  isMobile,
  loading = false,
  onFinishRendering = () => {},
}: {
  showPreview: boolean;
  loading?: boolean;
  emailHtml: string;
  emailHtmlCounter?: number;
  view: "desktop" | "mobile";
  setView: (view: "desktop" | "mobile") => void;
  isMobile: boolean;
  onFinishRendering?: () => void;
}) => {
  const isMobilePreviewOn = showPreview && isMobile;
  const ref = useRef<HTMLIFrameElement>(null);
  const emailPreviewWidth =
    view === "desktop" ? "600px" : isMobile ? "100%" : "360px";

  const iframeReady = useRef(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "rendered") {
        onFinishRendering();
      } else if (event.data && event.data.type === "iframe-ready") {
        iframeReady.current = true;
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onFinishRendering]);

  useEffect(() => {
    if (!emailHtml) return;

    let retryTimeout: NodeJS.Timeout | null = null;
    let attempts = 0;
    const maxAttempts = 20; // Max 1 second of retries

    const tryPostMessage = () => {
      const iframe = ref.current;

      if (!iframe || !iframe.contentWindow || !iframeReady.current) {
        attempts++;
        if (attempts < maxAttempts) {
          retryTimeout = setTimeout(tryPostMessage, 50);
        }
        return;
      }

      try {
        // Post the message once iframe is fully ready
        iframe.contentWindow.postMessage(
          { type: "html", value: emailHtml },
          "*"
        );
      } catch (error) {
        // If posting fails, retry
        attempts++;
        if (attempts < maxAttempts) {
          retryTimeout = setTimeout(tryPostMessage, 50);
        }
      }
    };

    // Small delay to ensure iframe is fully loaded
    const initialDelay = setTimeout(tryPostMessage, 10);

    return () => {
      clearTimeout(initialDelay);
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [emailHtml, emailHtmlCounter]);

  useEffect(() => {
    // Reset iframe ready state when preview state changes
    iframeReady.current = false;
  }, [showPreview, isMobile]);

  return (
    <Box flex="1" bg="#C9C9C9" w="100%" h="100%" flexShrink={0}>
      <AnimatePresence>
        {(showPreview || !isMobile) && (
          <motion.div
            initial={{ y: isMobile ? "100%" : 0, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: isMobile ? "100%" : 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "static",
              borderTop: "1px solid transparent",
              display: "flex",
              flexDirection: "column",
              bottom: 0,
              height: "100%",
              left: 0,
              right: 0,
              zIndex: 999,
              backgroundColor: isMobilePreviewOn ? "white" : "transparent",
            }}
          >
            {!isMobile && (
              <Flex p={4} justifyContent="center" alignItems="center">
                <Flex justifyContent="center" gap={2}>
                  <Button
                    variant={view === "desktop" ? "solid" : "outline"}
                    onClick={() => setView("desktop")}
                  >
                    <Icon as={FaDesktop} />
                  </Button>
                  <Button
                    variant={view === "mobile" ? "solid" : "outline"}
                    onClick={() => setView("mobile")}
                  >
                    <Icon as={FaMobileAlt} />
                  </Button>
                </Flex>
              </Flex>
            )}
            <Box
              borderTop="1px solid"
              borderColor="black"
              borderTopColor="blackAlpha.400"
            />
            <Flex
              px={isMobile ? 0 : 12}
              py={isMobile ? 0 : 6}
              display="flex"
              flexGrow={1}
              justifyContent="center"
            >
              <Box
                borderRadius="10px !important"
                overflow="hidden"
                backgroundColor="white"
                boxShadow="0px 0px 15px rgba(0, 0, 0, 0.05)"
                width={isMobile ? "100%" : emailPreviewWidth}
                minWidth={isMobile ? "100%" : emailPreviewWidth}
                position="relative"
                h="100%"
              >
                {loading ? (
                  <Box
                    display="flex"
                    w="100%"
                    position="absolute"
                    h="100%"
                    zIndex={1000}
                    justifyContent="center"
                    alignItems="center"
                    backgroundColor="rgba(255, 255, 255, 0.8)"
                  >
                    <Spinner />
                  </Box>
                ) : null}
                <iframe
                  ref={ref}
                  srcDoc={initialSrcDoc}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                    border: "none",
                  }}
                  title="Live Preview"
                />
              </Box>
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default CreateCampaignPreview;
